/* @refresh reload */
import { render } from "solid-js/web";

import { App } from "@/app";
import "@/lib/client-telemetry";
import "@/styles/global.css";

// PWA: auto-update with a SINGLE reload. `sw.js` (Workbox) already calls `skipWaiting()`
// upon receiving `{type:"SKIP_WAITING"}`; here, when a new SW is INSTALLED and a controller exists
// (an update, not the first install), we request it → it takes control → `controllerchange`
// → we reload ONCE. One user refresh is enough (previously the new SW stayed `waiting`).
// Intentionally dependency-free (avoids workbox-window). To make it FULLY automatic, see
// infra/PWA_UPDATES.md (reload inactive tabs without user action). Tauri has no SW → no-op.
if ("serviceWorker" in navigator) {
  let reloading = false;
  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (reloading) {
      return;
    }
    reloading = true;
    globalThis.location.reload();
  });
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js", { scope: "/" }).then((reg) => {
      // In environments where the SW is blocked (e.g. Playwright with
      // `serviceWorkers: "block"`) or denied, `register()` may resolve without
      // a usable registration. Without this guard, `reg.addEventListener`
      // would throw "Cannot read properties of undefined" as a pageerror.
      if (!reg) {
        return undefined;
      }
      reg.addEventListener("updatefound", () => {
        const next = reg.installing;
        if (!next) {
          return;
        }
        next.addEventListener("statechange", () => {
          // Only on UPDATE (a controller already exists); do not reload on the first install.
          if (next.state === "installed" && navigator.serviceWorker.controller) {
            // ServiceWorker.postMessage(message, transfer): the 2nd arg is the
            // transferables list, not a targetOrigin (that is Window.postMessage).
            // We pass [] explicitly to satisfy the linter rule without changing semantics.
            next.postMessage({ type: "SKIP_WAITING" }, []);
          }
        });
      });
      return undefined;
    });
  });
}

const root = document.querySelector("#root");

if (!root) {
  throw new Error("Root element #root not found in index.html");
}

render(() => <App />, root);

function dismissAppLoader() {
  const loader = document.querySelector("#app-loader");
  if (!loader) {
    return;
  }
  loader.classList.add("app-loader--hidden");
  const LOADER_FADE_MS = 300;
  setTimeout(() => {
    loader.remove();
  }, LOADER_FADE_MS);
}
requestAnimationFrame(() => {
  requestAnimationFrame(dismissAppLoader);
});

// Tell the index.html boot watchdog the app mounted: cancels the self-heal
// reload and clears its one-shot guard (so a future stale-deploy can recover).
(globalThis as typeof globalThis & { __appBootOk?: () => void }).__appBootOk?.();
